import { cn, Input as HeroInput, InputProps as HeroInputProps } from '@heroui/react'
import { motion } from 'framer-motion'
import { useState } from 'react'

export interface InputPasswordProps
  extends Omit<HeroInputProps, 'classNames' | 'type' | 'onChange'> {
  label?: string
  placeholder?: string
  classNameContainer?: string
  variant?: 'bordered' | 'flat' | 'faded' | 'underlined'
  classNames?: {
    label?: string[]
    input?: string[]
    innerWrapper?: string
    inputWrapper?: string[]
  }
  labelPlacement?: 'outside' | 'inside' | 'outside-left'
  name?: string
  isRequired?: boolean
  value?: string
  onChange?: (value: string) => void
  onBlur?: () => void
  errorMessage?: string
}

const InputPassword = ({
  classNameContainer,
  variant = 'bordered',
  classNames,
  labelPlacement = 'outside',
  name,
  isRequired = false,
  value,
  onChange,
  onBlur,
  errorMessage,
  ...props
}: InputPasswordProps) => {
  const [isVisible, setIsVisible] = useState(false)

  const toggleVisibility = () => setIsVisible(!isVisible)

  return (
    <motion.div className={cn(classNameContainer)}>
      <HeroInput
        fullWidth
        className="h-full"
        errorMessage={errorMessage}
        isRequired={isRequired}
        labelPlacement={labelPlacement}
        name={name}
        radius="sm"
        type={isVisible ? 'text' : 'password'}
        value={value}
        variant={variant}
        classNames={{
          label: [
            'text-gray-900',
            'dark:text-foreground',
            'group-data-[filled-within=true]:text-gray-900',
            'dark:group-data-[filled-within=true]:text-white',
            'group-data-[invalid=true]:text-danger',
            'dark:group-data-[invalid=true]:text-danger',
            ...(classNames?.label || []),
          ],
          input: [
            'bg-transparent',
            'text-gray-900',
            'dark:text-white',
            'placeholder:text-gray-500',
            'dark:placeholder:text-white/60',
            'group-data-[invalid=true]:placeholder:text-danger',
            ...(classNames?.input || []),
          ],
          innerWrapper: 'bg-transparent',
          inputWrapper: [
            'bg-gray-50',
            'dark:bg-[#1e1e20]',
            'border-gray-200',
            'dark:border-default-600',
            'hover:bg-gray-100',
            'dark:hover:bg-[#1e1e20]/70',
            'group-data-[focus=true]:bg-gray-100',
            'dark:group-data-[focus=true]:bg-[#1e1e20]/50',
            'group-data-[focus=true]:border-primary',
            'dark:group-data-[focus=true]:border-primary',
            '!cursor-text',
            ...(classNames?.inputWrapper || []),
          ],
        }}
        endContent={
          <button className="focus:outline-none" type="button" onClick={toggleVisibility}>
            {isVisible ? (
              <svg
                className="text-2xl text-default-400 pointer-events-none"
                fill="none"
                height="1em"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                width="1em"
              >
                <path d="m1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            ) : (
              <svg
                className="text-2xl text-default-400 pointer-events-none"
                fill="none"
                height="1em"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                width="1em"
              >
                <path d="m1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <path d="m3 3 18 18" />
              </svg>
            )}
          </button>
        }
        onBlur={onBlur}
        onValueChange={onChange}
        {...props}
      />
    </motion.div>
  )
}

export default InputPassword
